import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { Pressable, ScrollView, SxProp, View, useDripsyTheme } from 'dripsy';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { Icon } from '@/design-system/components/icons/Icon';
import { CreateCardAccountStackRoutes } from '@/features/card-account/screens/create-card-account/type';
import { Moment } from 'moment-timezone';
import { DateRangePicker } from '@/design-system/components/dateRangePicker';
import moment from 'moment-timezone';
import { useReportingTransactions } from '@/features/account-management/screens/list/hooks/useListAccounts.hook';
import { Linking } from 'react-native';
import { isEmpty } from 'lodash';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { CardAccountTypeFilters } from '@/features/card-account/options';
import { CardAccountTypeColor } from '@/features/card-account/enum';
import { formatAmountByDecimals } from '@/utils/numberFormatting';

export interface DownloadCardAccountTransactionReportProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  creditLineId?: string;
}

export const DownloadCardAccountTransactionReportWebScreen: React.FC<DownloadCardAccountTransactionReportProps> = (props) => {
  const { onClose, onLayout, creditLineId } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, btnView, textMb, btn, account } = sxStyles;
  const { theme } = useDripsyTheme();

  const navigationAny = useNavigation<any>();

  const [wallet, setWallet] = useState<any>();
  const [dateRangeProps, setDateRangeProps] = useState<{ from: Moment | null; to: Moment | null }>({
    from: null,
    to: null
  });
  const handleChangeDateRange = (value: { from: Moment | null; to: Moment | null }) => {
    setDateRangeProps(value);
  };

  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { reportingCardaccountsTransactions } = useReportingTransactions();
  const downloadTransactionReportFunc = () => {
    setIsLoading(true);
    reportingCardaccountsTransactions({
      dateRangeFrom: dateRangeProps?.from ? moment(dateRangeProps?.from)?.toISOString() : undefined,
      dateRangeTo: dateRangeProps?.to ? moment(dateRangeProps?.to)?.toISOString() : undefined,
      creditLineId: creditLineId || wallet?.id
    })
      .then((response) => {
        setIsLoading(false);
        if (response.data && response.data?.url) {
          // Linking.openURL(response.data?.url);
          const newWindow = window.open(response.data?.url, '_blank');
          if (newWindow) newWindow.opener = null;
          onClose();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setAlertMessage(error);
      });
  };

  const [viewShow, setViewShow] = useState(false);
  const onChangeView = () => {
    setViewShow(!viewShow);
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary',
        overflow: 'visible',
        borderRadius: theme.space?.$radius2 as number
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        sx={{
          borderTopLeftRadius: theme.space?.$radius2 as number,
          borderTopRightRadius: theme.space?.$radius2 as number,
          overflow: 'hidden'
        }}
        title={t('card-account-management::account-page.button--download-transaction-report')}
        closeModal={onClose}
      />
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('card-account-management::account-page.text--download-following-filter')}
        </TextTitle>
        <View sx={{ gap: '$5' }}>
          {!creditLineId ? (
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('card-account-management::account-page.text--account')}</TextCaption>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(creditLineId ? RootStackRoutes.AccountManagement : RootStackRoutes.CardAccount, {
                    screen: AccountManagementStackRoutes.DownloadTransactionReport,
                    params: {
                      screen: CreateCardAccountStackRoutes.SelectCardAccount,
                      onLayout,
                      onSelect: (data: any) => {
                        setWallet(data);
                      },
                      onChangeView: onChangeView,
                      borderRadiusFlag: true
                    }
                  });
                }}>
                <View sx={{ ...account, ...(wallet ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {wallet ? (
                      <View sx={{ gap: '$1' }}>
                        <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <TextBody semiBold sx={{ color: '$textPrimary' }}>
                            {wallet?.label}
                          </TextBody>
                          <TextBody semiBold sx={{ color: '$textPrimary' }}>
                            {formatAmountByDecimals({ value: Math.abs(wallet?.balance), suffix: wallet?.assetSymbol })}{' '}
                            {wallet?.balance < 0 && '[CR]'}
                          </TextBody>
                        </View>
                        <View sx={{ flexDirection: 'row', gap: '$4', alignItems: 'center' }}>
                          {wallet?.autopayEnabled && (
                            <View
                              sx={{
                                paddingX: '$2',
                                paddingY: '$1',
                                width: 'fit-content',
                                borderRadius: 100,
                                backgroundColor: theme.colors?.$tagGreenBackground as string
                              }}>
                              <TextCaption sx={{ color: theme.colors?.$tagGreenText as string }}>
                                {t('card-account::landing-page.text--utgl-autopay-enabled')}
                              </TextCaption>
                            </View>
                          )}
                          <View
                            sx={{
                              paddingX: '$2',
                              paddingY: '$1',
                              width: 'fit-content',
                              borderRadius: 100,
                              backgroundColor: theme.colors?.$tagBlackBackground as string
                            }}>
                            <TextCaption sx={{ color: theme.colors?.$tagBlackText as string }}>{wallet?.assetSymbol}</TextCaption>
                          </View>
                          <View
                            sx={{
                              paddingX: '$2',
                              paddingY: '$1',
                              width: 'fit-content',
                              borderRadius: 100,
                              backgroundColor: theme.colors[`$tag${CardAccountTypeColor[wallet?.type as ColorTypes]}Background`] as string
                            }}>
                            <TextCaption
                              sx={{ color: theme.colors[`$tag${CardAccountTypeColor[wallet?.type as ColorTypes]}Text`] as string }}>
                              {CardAccountTypeFilters(t).find((rec) => rec.value === wallet?.type)?.label}
                            </TextCaption>
                          </View>
                        </View>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>
                        {t('card-account::credit-card-new-account.glossary.select-account')}
                      </TextCaption>
                    )}
                    <View sx={{ flexDirection: 'row', gap: '$1' }}>
                      <Icon name="arrowRight" />
                      {wallet ? (
                        <UniIconButton
                          icon="cross"
                          onPress={() => {
                            setWallet(undefined);
                          }}
                        />
                      ) : undefined}
                    </View>
                  </View>
                </View>
              </Pressable>
            </View>
          ) : undefined}
          <View sx={{ gap: 4 }}>
            <TextCaption semiBold>{t('earn-invest::glossary.date-range')}</TextCaption>
            <View sx={{ height: 36, width: '$80' }}>
              <DateRangePicker
                dateRangeProps={dateRangeProps}
                onValueChange={(value) => {
                  handleChangeDateRange({
                    ...dateRangeProps,
                    from: value.from ? moment(value.from) : null,
                    to: value.to ? moment(value.to) : null
                  });
                }}
              />
            </View>
          </View>
        </View>
        {viewShow && <View sx={{ height: '$1', width: '100%' }} />}
        {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::account-page.text--download')}
            onPress={downloadTransactionReportFunc}
            loading={isLoading}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: '100%',
    height: 44
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
